import AnimatedCaret from 'components/base/AnimatedCaret';
import Checkbox from 'components/base/Checkbox/Checkbox';
import Menu from 'components/base/Menu/Menu';
import Box from 'components/elements/Box';
import Span from 'components/elements/span';
import playlistCustomers from 'maps/customer-name-id';
import React, { useMemo, useState } from 'react';
import { ICustomer } from 'redux/reducers/customers';
import {
  ListItemBox,
  AllDSPIcon,
  ButtonBox,
  LabelSpan,
  StyledDropdown,
} from './styles';

interface IListItemProps {
  item: any;
  isActive?: boolean;
  selectedItems: IItem[];
}

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, selectedItems } = props;

  return (
    <ListItemBox display="flex" alignItems="center">
      <Box
        alignSelf="center"
        display="inline-block"
        marginLeft="2px"
        marginRight="-2px"
      >
        <Checkbox
          enabledText="Disable"
          disabledText="Enable"
          isChecked={!!isActive}
          isMinus={
            item.value === 'all' && !isActive && selectedItems.length > 0
          }
          insideDropdown
        />
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {item.value === 'all' && <AllDSPIcon width="13px" height="13px" />}
        {item.displayName}
      </Span>
    </ListItemBox>
  );
};

const getButtonWithDefaultLabel = (): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    const { selectedItem, selectedItems, isOpen, defaultLabel } = props;

    const hasSelectedValue =
      (selectedItem || selectedItems?.length > 0) &&
      !selectedItems?.map((i: IItem) => i.value).includes('all');

    return (
      <ButtonBox
        py={1}
        px={2}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        alignContent="stretch"
        isGreen={hasSelectedValue && !isOpen}
      >
        {selectedItems?.length > 1 ? (
          <LabelSpan
            alignSelf="auto"
            marginRight="8px"
            fontWeight={600}
            style={{ whiteSpace: 'nowrap' }}
          >
            {`DSP (${selectedItems.length})`}
          </LabelSpan>
        ) : (
          <LabelSpan alignSelf="auto" marginRight="8px" fontWeight={600}>
            {selectedItems &&
              selectedItems.length === 1 &&
              selectedItems[0].value === 'all' && <AllDSPIcon />}
            {selectedItems && selectedItems.length === 1
              ? selectedItems[0].label || selectedItems[0].displayName
              : defaultLabel}
          </LabelSpan>
        )}

        <Span marginLeft="auto" alignSelf="auto">
          <AnimatedCaret isPointingUp={isOpen} />
        </Span>
      </ButtonBox>
    );
  };
  return LabeledButton;
};

interface IItem {
  displayName: string;
  value: string | number;
}

const allItem: IItem = {
  displayName: 'All DSPs',
  value: 'all',
};

interface IDspFilterProps {
  customers: ICustomer[];
  setDspFilter: (dspIds: string[]) => void;
}

const DspFilter: React.FC<IDspFilterProps> = (props) => {
  const { customers, setDspFilter } = props;

  const [selectedItems, setSelectedItems] = useState<any[]>([allItem]);

  const items = useMemo(() => {
    return customers
      ? Object.values(playlistCustomers).map((playlistId: number) => {
          const customer = customers?.find(
            (c: ICustomer) => c.id === playlistId
          );
          return customer
            ? {
                displayName: customer.displayName,
                value: customer.id,
              }
            : undefined;
        })
      : [];
  }, [customers]);

  const allItems = [
    allItem,
    ...items.sort((a: any, b: any): any =>
      a.displayName.localeCompare(b.displayName)
    ),
  ];

  const onChange = (item: IItem): void => {
    const isAllSelected =
      selectedItems?.length === 1 && selectedItems[0].value === 'all';
    const itemValues = isAllSelected ? allItems : selectedItems;
    const currentDSPList = itemValues?.map((sItem?: IItem) => sItem?.value);

    // All DSPs item
    if (item.value === 'all') {
      if (currentDSPList?.includes(item.value) || selectedItems.length) {
        // uncheck all
        setSelectedItems([]);
      } else {
        // check all
        setSelectedItems([allItem]);
      }
      setDspFilter([]);
    } else {
      const isChecked = currentDSPList?.includes(item.value);
      let newList: (IItem | undefined)[] = [item];
      if (isChecked && currentDSPList?.length) {
        // remove from selected list
        newList = itemValues.filter(
          (dsp?: IItem) => dsp?.value !== item.value && dsp?.value !== 'all'
        );
      } else if (currentDSPList?.length) {
        // add to selected list
        newList = [...itemValues, item];
      }

      const dspWithoutUndefined: any = newList.filter(
        (dsp: IItem | undefined) => dsp !== undefined
      );

      if (dspWithoutUndefined.length === items.length) {
        // selected all items
        setSelectedItems([allItem]);
        setDspFilter([]);
      } else {
        setSelectedItems(dspWithoutUndefined);
        setDspFilter(
          dspWithoutUndefined.map((dsp: IItem) => dsp?.value.toString())
        );
      }
    }
  };

  const onClose = (): void => {
    if (selectedItems.length === 0) setSelectedItems([allItem]);
  };

  return (
    <StyledDropdown>
      <Menu
        items={allItems}
        selectedItems={selectedItems}
        multipleSelections={selectedItems}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange={(): void => {}}
        onSelect={onChange}
        itemToString={(item: any): string => item?.value}
        Button={getButtonWithDefaultLabel()}
        Item={ListItem}
        isMultipleSelection
        onClose={onClose}
      />
    </StyledDropdown>
  );
};

export default DspFilter;
